import usePrivateAxios from '@/common/api/usePrivateAxios'
import { ErrorResponse } from '@/common/types/errorResponse'
import { TicketData } from '@/common/types/support/ticket.type'
import { InternalSupportTicketValidationSchema } from '@/features/support/internal/schemas/create.schema'
import { SupportTicketAvailableUserWithRelationType } from '@/features/support/internal/types/supportTicketAvailableUser.type'
import { useMutation } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

interface UseCreateInternalSupportTicketProps {
  creator_id?: string
  onSuccess: (response: AxiosResponse) => void
  onError: (error: ErrorResponse) => void
}

export const useCreateInternalSupportTicket = ({
  creator_id,
  onSuccess,
  onError,
}: UseCreateInternalSupportTicketProps) => {
  const privateAxios = usePrivateAxios()

  return useMutation({
    mutationKey: ['createInternalSupportTicket'],
    mutationFn: async ({
      form,
      users,
      supportTickets,
    }: {
      form: InternalSupportTicketValidationSchema
      users: SupportTicketAvailableUserWithRelationType[]
      supportTickets: TicketData[]
    }) => {
      const payload = {
        reason_id: form.reason.value,
        subject_line: form.subject_line,
        first_message_attributes: {
          creator_id,
          creator_type: 'Admin::User',
          content: form.content,
          documents_attachments_attributes: form.documents?.length
            ? form.documents.map((document) => ({ blob_id: document.id }))
            : undefined,
        },
        user_tickets_attributes: users.map((user) => ({
          user_id: user.id,
          user_type: 'Admin::User',
          relation_type: user.relationType,
        })),
        outgoing_ticket_linkings_attributes: supportTickets.map((ticket) => ({
          target_ticket_id: ticket.id,
        })),
      }

      return privateAxios.post('/support/tickets/create_internal_ticket', { support_ticket: payload })
    },
    onSuccess,
    onError,
  })
}
